/**
 * TrackerForm — the pairing sheet, now a shell over the shared write.
 *
 * IT IS UNMOUNTED FROM THE SETUP CANVAS AND STILL MOUNTED ON INSIGHTS. `ContractInsights` opens
 * it, so it cannot be deleted; the setup page pairs the same two slots in place, as picker rows.
 * The read already came from `setup/data/trackers.ts`; the write now does too, because the
 * delete-or-repoint-or-pair branch set is exactly the kind of three-way rule that drifts the first
 * time either copy is touched.
 */

import { useState, useEffect, useMemo } from 'react';
import { captureError } from "@/lib/logger";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import TrackerSelector from '@/components/ui/TrackerSelector';
import {
    fetchVehicleTrackers,
    saveVehicleTracker,
    vehicleTrackersKey,
} from '@/components/contracts/setup/data/trackers';

interface TrackerFormProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    vehicleId: number;
    onSuccess?: () => void;
}

export default function TrackerForm({
    open,
    onOpenChange,
    vehicleId,
    onSuccess,
}: TrackerFormProps) {
    const queryClient = useQueryClient();
    const [primaryTrackerId, setPrimaryTrackerId] = useState<number | null>(null);
    const [secondaryTrackerId, setSecondaryTrackerId] = useState<number | null>(null);
    const [showCloseConfirm, setShowCloseConfirm] = useState(false);

    // Fetch existing trackers for this vehicle — the shared read, so the Vehicle area's
    // tracker rows and this sheet name the same trackers out of one cache entry.
    const { data: existingTrackers, isLoading: isLoadingExisting } = useQuery({
        queryKey: vehicleTrackersKey(vehicleId),
        queryFn: () => fetchVehicleTrackers(vehicleId),
        enabled: open && !!vehicleId,
    });

    // Initialize state from existing trackers when sheet opens
    useEffect(() => {
        if (open && existingTrackers) {
            const primary = existingTrackers.find(t => t.type === 'primary');
            const secondary = existingTrackers.find(t => t.type === 'secondary');
            setPrimaryTrackerId(primary?.tracker_id || null);
            setSecondaryTrackerId(secondary?.tracker_id || null);
        }
    }, [open, existingTrackers]);

    // Original values for dirty checking
    const originalPrimaryId = useMemo(() => {
        return existingTrackers?.find(t => t.type === 'primary')?.tracker_id || null;
    }, [existingTrackers]);

    const originalSecondaryId = useMemo(() => {
        return existingTrackers?.find(t => t.type === 'secondary')?.tracker_id || null;
    }, [existingTrackers]);

    const isDirty = useMemo(() => {
        return primaryTrackerId !== originalPrimaryId || secondaryTrackerId !== originalSecondaryId;
    }, [primaryTrackerId, secondaryTrackerId, originalPrimaryId, originalSecondaryId]);

    const mutation = useMutation({
        // THE WRITE IS `saveVehicleTracker`, THE SAME ONE THE SETUP CANVAS CALLS. This sheet is
        // still mounted on `ContractInsights` and the setup page pairs trackers in place; two
        // copies of the delete/repoint/pair branch set is how one slot ends up with two junction
        // rows. Only a slot that actually moved is written, which is what the sheet always did.
        mutationFn: async () => {
            const existingPrimary = existingTrackers?.find(t => t.type === 'primary');
            const existingSecondary = existingTrackers?.find(t => t.type === 'secondary');

            if (primaryTrackerId !== originalPrimaryId) {
                await saveVehicleTracker({
                    vehicleId,
                    type: 'primary',
                    existingPairingId: existingPrimary?.id ?? null,
                    trackerId: primaryTrackerId,
                });
            }

            if (secondaryTrackerId !== originalSecondaryId) {
                await saveVehicleTracker({
                    vehicleId,
                    type: 'secondary',
                    existingPairingId: existingSecondary?.id ?? null,
                    trackerId: secondaryTrackerId,
                });
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['prep-status'] });
            queryClient.invalidateQueries({ queryKey: ['available-trackers'] });
            queryClient.invalidateQueries({ queryKey: vehicleTrackersKey(vehicleId) });
            queryClient.invalidateQueries({ queryKey: ['contract-insights'] });
            toast.success('Trackers saved');
            resetAndClose();
            onSuccess?.();
        },
        onError: (error) => {
            captureError(error);
            toast.error('Failed to save trackers');
        },
    });

    const handleClose = () => {
        if (isDirty) {
            setShowCloseConfirm(true);
        } else {
            resetAndClose();
        }
    };

    const resetAndClose = () => {
        setShowCloseConfirm(false);
        onOpenChange(false);
    };

    return (
        <>
            <Sheet open={open} onOpenChange={handleClose}>
                <SheetContent
                    side="right"
                    className="!inset-y-4 !right-4 !h-auto !w-[35vw] !max-w-none rounded-2xl flex flex-col shadow-2xl border bg-background"
                >
                    <SheetHeader>
                        <SheetTitle>Manage Trackers</SheetTitle>
                    </SheetHeader>

                    <div className="flex-1 flex flex-col gap-6 mt-6">
                        {isLoadingExisting ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                            </div>
                        ) : (
                            <div className="flex-1 space-y-6 overflow-y-auto">
                                {/* Primary Tracker */}
                                <div className="space-y-2">
                                    <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                        Primary Tracker *
                                    </Label>
                                    <TrackerSelector
                                        value={primaryTrackerId}
                                        onChange={(id) => setPrimaryTrackerId(id)}
                                        placeholder="Select primary tracker..."
                                        excludeIds={secondaryTrackerId ? [secondaryTrackerId] : []}
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        The main GPS tracker for this vehicle
                                    </p>
                                </div>

                                {/* Secondary Tracker */}
                                <div className="space-y-2">
                                    <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                        Secondary Tracker
                                    </Label>
                                    <TrackerSelector
                                        value={secondaryTrackerId}
                                        onChange={(id) => setSecondaryTrackerId(id)}
                                        placeholder="Select secondary tracker..."
                                        excludeIds={primaryTrackerId ? [primaryTrackerId] : []}
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        Optional backup tracker if the primary fails
                                    </p>
                                </div>
                            </div>
                        )}

                        <div className="pt-4 border-t flex justify-end gap-2">
                            <Button type="button" variant="outline" onClick={handleClose}>
                                Cancel
                            </Button>
                            <Button
                                onClick={() => mutation.mutate()}
                                disabled={mutation.isPending || !primaryTrackerId}
                            >
                                {mutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                Save
                            </Button>
                        </div>
                    </div>
                </SheetContent>
            </Sheet>

            <AlertDialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Discard changes?</AlertDialogTitle>
                        <AlertDialogDescription>
                            You have unsaved changes. Are you sure you want to close without saving?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Continue Editing</AlertDialogCancel>
                        <AlertDialogAction onClick={resetAndClose} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            Discard
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
